/* =========================================================================
   FC — Variant-Aware Media Gallery (desktop only)
   ---------------------------------------------------------------------------
   Behaviour (agreed with client):
   - Default / first variant  -> show the default gallery (the built-in
                                 "minimal gallery" 5-item cap stays in force).
   - Any other variant        -> show ONLY the media whose alt text contains
                                 the variant's exact SKU, uncapped.
                                 Match rule: the alt text must contain the SKU
                                 string verbatim (case-sensitive, no
                                 normalisation). SKU is used (not the variant
                                 title) because variant names collide across
                                 products with added prefixes/suffixes.
   - No matching media         -> fall back to the default gallery.

   Notes:
   - Desktop only. The mobile gallery is a separate <media-gallery> and is left
     untouched, matching the desktop-only 5-item requirement.
   - Video / model media is filtered by the same alt-match rule as images
     (see FILTER_NON_IMAGE_MEDIA): a video with no alt matching the variant is
     hidden on non-default variants, and always shown for the default variant.
   - If the theme's built-in "variant group images" feature is enabled we bail
     out entirely so the two systems never fight over the gallery.
   - The 5-item cap is a positional CSS rule (:nth-child(n+6) on
     [data-minimal-gallery="true"]). It cannot coexist with content-based
     hiding, so on a filtered variant we switch the attribute off and drive
     visibility with the .fc-media-hidden class instead.

   Developer: Alfie / Jon
   Updated:   2026-07-10
   ========================================================================= */
(function () {
  'use strict';

  /* When true, videos / models are filtered by the same alt-match rule as
     images: on a non-default variant a video is hidden unless its alt text
     matches the variant. Flip to false to keep all videos/models visible
     regardless of variant. */
  var FILTER_NON_IMAGE_MEDIA = true;

  /* The needle to look for in each alt: the variant's exact SKU (trimmed). */
  function skuNeedle(variant) {
    return variant && variant.sku ? String(variant.sku).trim() : '';
  }

  function altOf(cell) {
    var node = cell.querySelector('[data-media-alt]');
    if (node && node.dataset.mediaAlt) return node.dataset.mediaAlt;
    var img = cell.querySelector('img');
    return img ? img.getAttribute('alt') || '' : '';
  }

  function mediaTypeOf(cell) {
    if (cell.dataset && cell.dataset.mediaType) return cell.dataset.mediaType;
    var m = cell.className.match(/media-type-([\w-]+)/);
    return m ? m[1] : '';
  }

  /* Exact match: the alt text contains the SKU string verbatim
     (case-sensitive, no normalisation). */
  function altMatches(cell, needle) {
    if (!needle) return false;
    return altOf(cell).indexOf(needle) !== -1;
  }

  /* ----------------------------------------------------------------------- */

  function setupGallery(gallery) {
    if (gallery.__fcGallery) return gallery.__fcGallery;

    /* Original cap state: "true" when the merchant enabled the minimal
       gallery, otherwise null (no cap — the default is all images). */
    var originalMinimal = gallery.getAttribute('data-minimal-gallery');

    function mainCells() {
      return gallery.querySelectorAll('.m-product-media--item');
    }
    function thumbCells() {
      return gallery.querySelectorAll(
        '.m-product-media--slider__thumbnails .swiper-slide'
      );
    }

    function clearHidden() {
      gallery
        .querySelectorAll('.fc-media-hidden')
        .forEach(function (el) {
          el.classList.remove('fc-media-hidden');
        });
    }

    /* Re-sync any Swiper instance (slider layouts) and the grid "5 / 15"
       counter, which recalculates on window resize. Grid layout has no
       slider, so the slider calls are simply skipped. */
    function refresh() {
      if (gallery.slider && typeof gallery.slider.update === 'function') {
        try {
          gallery.slider.update();
          gallery.slider.slideTo(0, 0);
        } catch (e) {}
      }
      if (gallery.navSlider && typeof gallery.navSlider.update === 'function') {
        try {
          gallery.navSlider.update();
        } catch (e) {}
      }
      window.dispatchEvent(new Event('resize'));
    }

    function showDefault() {
      if (originalMinimal === null) {
        gallery.removeAttribute('data-minimal-gallery');
      } else {
        gallery.setAttribute('data-minimal-gallery', originalMinimal);
      }
      clearHidden();
      refresh();
    }

    function filterByVariant(needle) {
      /* No SKU on the variant -> nothing to match on; keep the default view. */
      if (!needle) {
        showDefault();
        return;
      }

      var cells = Array.prototype.slice.call(mainCells());

      /* Count matches first so we can honour the zero-match fallback before
         mutating anything. */
      var matches = 0;
      cells.forEach(function (cell) {
        var isImage = mediaTypeOf(cell) === 'image';
        if (!isImage && !FILTER_NON_IMAGE_MEDIA) return;
        if (altMatches(cell, needle)) matches++;
      });

      if (matches === 0) {
        showDefault();
        return;
      }

      /* Disable the positional 5-item cap; drive visibility by content. */
      gallery.setAttribute('data-minimal-gallery', 'false');

      function apply(cell) {
        var isImage = mediaTypeOf(cell) === 'image';
        var keep = !isImage && !FILTER_NON_IMAGE_MEDIA
          ? true
          : altMatches(cell, needle);
        cell.classList.toggle('fc-media-hidden', !keep);
      }

      cells.forEach(apply);
      Array.prototype.slice.call(thumbCells()).forEach(apply);
      refresh();
    }

    gallery.__fcGallery = {
      showDefault: showDefault,
      filter: function (variant) {
        filterByVariant(skuNeedle(variant));
      },
    };
    return gallery.__fcGallery;
  }

  /* ----------------------------------------------------------------------- */

  function init() {
    var main =
      document.querySelector('.m-main-product[data-product-id]') ||
      document.querySelector('[data-section-type="product-page"][data-product-id]');
    var gallery = document.querySelector('.m-product-media--desktop media-gallery');
    if (!gallery || !main) return;

    var productId = main.dataset.productId;

    /* Respect the built-in variant-group-images feature — never both. */
    if (gallery.getAttribute('data-enable-variant-group-images') === 'true') return;

    var controller = setupGallery(gallery);

    /* Variant lookup + default/first ids. */
    var variants = [];
    try {
      var node = document.querySelector('#productVariants[type="application/json"]');
      if (node) variants = JSON.parse(node.textContent) || [];
    } catch (e) {}

    var byId = {};
    variants.forEach(function (v) {
      byId[Number(v.id)] = v;
    });

    var idInput = document.querySelector(
      '.m-product-info--wrapper [name="id"], .m-main-product form [name="id"]'
    );
    var initialVariantId = idInput ? Number(idInput.value) : null;
    var firstVariantId = variants.length ? Number(variants[0].id) : null;

    function isDefault(id) {
      return (
        (initialVariantId && id === initialVariantId) ||
        (firstVariantId && id === firstVariantId)
      );
    }

    /* Dedupe: the theme event and the id-input change can both fire for the
       same selection; only act once per distinct variant id. */
    var lastId = null;

    function handle(variant) {
      if (!variant || variant.id == null) return;
      var id = Number(variant.id);
      if (id === lastId) return;
      lastId = id;

      if (isDefault(id)) {
        controller.showDefault();
        return;
      }
      /* Match on SKU; use whichever object actually carries one. Both the
         theme event (/product.js) and the embedded #productVariants JSON
         include `sku`, but guard in case the event payload lacks it. */
      var source = variant.sku ? variant : byId[id] || variant;
      controller.filter(source);
    }

    if (window.MinimogEvents && typeof window.MinimogEvents.subscribe === 'function') {
      window.MinimogEvents.subscribe(productId + '__VARIANT_CHANGE', handle);
    }
    if (idInput) {
      idInput.addEventListener('change', function () {
        var v = byId[Number(idInput.value)];
        if (v) handle(v);
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
